import { useState, type MouseEvent } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { randomboxApi } from '../../api/randombox.js';
import {
  ErrorBox,
  Pagination,
  Field,
  FilterBar,
  ProductCell,
  SortableTh,
  SearchInput,
  StatusBadge,
} from '../../components/ui.js';
import { Modal } from '../../components/Modal.js';
import { QueryState } from '../../components/QueryState.js';
import { GradeBadge } from '../../components/GradeBadge.js';
import { OrderStatusBadges } from './OrderStatusBadges.js';
import { useUrlUpdate, useUrlSearch } from '../../lib/hooks.js';
import { useTableSort } from '../../lib/useTableSort.js';
import { text, money, num, datetime } from '../../lib/format.js';

/**
 * 원본: randombox/order/randombox_order_list.php + randombox_load_more_data.php
 *
 * lottery 와 달리 티켓 범위 기본 제한이 없고, 송장번호 유무 필터를 추가했다
 * (원본 목록에서 배송 여부를 눈으로 훑어야 했던 것을 조건으로 뺐다).
 *
 * 원본의 배송 처리 중 비즈호스트 등록용 · 송장입력도 함께 옮겼다 (배송처리용 CJ
 * 업로드는 빠졌다). 원본은 iframe 모달로 열었지만 여기서는 비즈호스트는 이 화면
 * 안에서 끝내고, 파일 업로드가 필요한 송장입력만 전용 화면(/randombox/tracking)으로 뺐다.
 */
export function RbOrdersPage() {
  const qc = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  /**
   * 마지막으로 상세를 연 주문. 패널을 닫고 돌아왔을 때 "방금 어느 줄을 보고
   * 있었는지"가 남아 있게 그 줄만 옅게 표시한다 - 목록은 패널 뒤에서 그대로
   * 살아 있으므로 이 값도 닫을 때까지 유지된다.
   */
  const [lastOpened, setLastOpened] = useState<string | null>(null);
  const [bizhostOpen, setBizhostOpen] = useState(false);
  const [customerModal, setCustomerModal] = useState<{ idx: number; name: string } | null>(null);

  const page = Number(params.get('page') ?? '1');
  const perPage = Number(params.get('perPage') ?? '50');
  const sort = params.get('sort') ?? 'orderDate';
  const dir = (params.get('dir') ?? 'desc') as 'asc' | 'desc';
  const randomboxId = params.get('randomboxId') ?? '';
  const shipped = (params.get('shipped') ?? '') as 'yes' | 'no' | '';

  const update = useUrlUpdate(params, setParams);
  const [search, setSearch] = useUrlSearch(params, update);
  const debouncedSearch = params.get('q') ?? '';
  // 정렬 머리글이 아홉 개다 - 같은 세 인자를 매번 적지 않고 한 번만 묶는다.
  const th = useTableSort(sort, dir, update);

  const productOptions = useQuery({
    queryKey: ['randombox', 'productOptions'],
    queryFn: () => randomboxApi.productOptions(),
    staleTime: 10 * 60 * 1000,
  });

  const query = useQuery({
    queryKey: ['randombox', 'orders', { page, perPage, sort, dir, q: debouncedSearch, randomboxId, shipped }],
    queryFn: () =>
      randomboxApi.orders({
        page,
        perPage,
        sort,
        dir,
        q: debouncedSearch || undefined,
        randomboxId: randomboxId === '' ? undefined : Number(randomboxId),
        shipped: shipped || undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const rows = query.data?.items ?? [];
  /** 원본은 배송 반영이 끝난 건(delivery_reset_flg=1)에는 체크박스를 안 그린다. */
  const selectable = rows.filter((o) => !o.deliveryResetFlag && o.seq !== null);
  const allChecked = selectable.length > 0 && selectable.every((o) => selected.has(o.seq!));

  function toggleOne(seq: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(seq)) next.delete(seq);
      else next.add(seq);
      return next;
    });
  }

  function toggleAll() {
    setSelected(allChecked ? new Set() : new Set(selectable.map((o) => o.seq!)));
  }

  function afterWrite() {
    setSelected(new Set());
    setBizhostOpen(false);
    void qc.invalidateQueries({ queryKey: ['randombox', 'orders'] });
  }

  const applyDelivery = useMutation({
    mutationFn: () => randomboxApi.applyDelivery([...selected]),
    onSuccess: afterWrite,
  });

  const stopDelivery = useMutation({
    mutationFn: () => randomboxApi.stopDelivery([...selected]),
    onSuccess: afterWrite,
  });

  const writeError = applyDelivery.error ?? stopDelivery.error;

  const detailPath = (orderNo: string) => `/randombox/orders/${encodeURIComponent(orderNo)}`;

  /**
   * 줄 어디를 눌러도 상세가 열린다. 주문번호 링크와 같은 경로·같은 background
   * 상태로 가므로 패널로 열리는 것도, 닫았을 때 돌아오는 자리도 같다.
   *
   * 체크박스·수취인 버튼·링크처럼 자기 동작이 있는 것을 누른 경우와, 주문번호를
   * 복사하려고 글자를 끌어 고른 경우는 건드리지 않는다.
   */
  function openRow(e: MouseEvent<HTMLTableRowElement>, orderNo: string) {
    if ((e.target as HTMLElement).closest('a, button, input, label')) return;
    if (window.getSelection()?.toString()) return;
    setLastOpened(orderNo);
    navigate(detailPath(orderNo), { state: { background: location } });
  }

  const total = query.data?.total ?? 0;
  const rangeFrom = query.data && rows.length > 0 ? (query.data.page - 1) * query.data.perPage + 1 : 0;
  const rangeTo = rangeFrom > 0 ? rangeFrom + rows.length - 1 : 0;

  return (
    <section className="rb-orders">
      <header className="page-head">
        <h1 className="page-title">랜덤박스 주문 관리</h1>
        {query.isFetching && <span className="page-head__busy">갱신 중…</span>}
      </header>

      <FilterBar
        onReset={() => {
          setSearch('');
          setParams(new URLSearchParams(), { replace: true });
        }}
      >
        <Field label="랜덤박스">
          <select className="input" value={randomboxId} onChange={(e) => update({ randomboxId: e.target.value })}>
            <option value="">전체</option>
            {productOptions.data?.map((o) => (
              <option key={o.randomboxId ?? o.label} value={o.randomboxId ?? ''}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="배송 여부">
          <select className="input" value={shipped} onChange={(e) => update({ shipped: e.target.value })}>
            <option value="">전체</option>
            <option value="no">송장 미등록</option>
            <option value="yes">송장 등록됨</option>
          </select>
        </Field>

        <Field label="페이지당 건수">
          <select
            className="input"
            value={perPage}
            onChange={(e) => update({ perPage: e.target.value })}
          >
            <option value={50}>50건</option>
            {/* 서버 상한(db/sql.ts 의 MAX_PER_PAGE)과 같은 값이다. 더 크게 두면 400 이 난다. */}
            <option value={500}>500건</option>
          </select>
        </Field>

        <Field label="검색 (주문번호 · 수취인명 · 연락처)">
          <SearchInput
            value={search}
            placeholder="검색어 입력"
            onChange={setSearch}
          />
        </Field>

      </FilterBar>

      <div className="toolbar">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={selected.size === 0}
          onClick={() => setBizhostOpen(true)}
        >
          비즈호스트 등록용 ({selected.size})
        </button>
        <Link
          className="btn btn--ghost btn--sm"
          to={`/randombox/tracking${randomboxId ? `?randomboxId=${randomboxId}` : ''}`}
        >
          송장입력
        </Link>
        {selected.size > 0 && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setSelected(new Set())}>
            선택 해제
          </button>
        )}
        {/* 지금 보고 있는 범위. 아래 페이지네이션까지 내려가지 않아도 전체 건수를 안다. */}
        {rangeFrom > 0 && (
          <span className="rb-orders__range">
            {rangeFrom.toLocaleString()}–{rangeTo.toLocaleString()} / 총 <strong>{total.toLocaleString()}</strong>건
          </span>
        )}
      </div>

      {/*
        배송 처리 실패는 **한 곳에만** 보여준다. 예전에는 같은 오류가 툴바
        아래와 모달 안에 동시에 떴다 (문구도 서로 달랐다). 모달이 열려 있으면
        모달 안에서만, 닫혀 있으면 목록 위에서 보여준다.
      */}
      {writeError && !bizhostOpen && <ErrorBox error={writeError} />}

      {bizhostOpen && (
        <Modal title="비즈호스트 등록용 · 배송 처리" onClose={() => setBizhostOpen(false)}>
          <p className="hint hint--warn">
            선택한 <strong>{selected.size}건</strong>이 대상입니다. 엑셀을 먼저 내려받아 비즈호스트에
            등록한 뒤 <strong>배송 반영</strong>을 눌러주세요. 배송 반영을 하면 목록에서 다시 고를 수
            없습니다.
          </p>
          <div className="form-grid">
            <a
              className="btn btn--primary btn--block"
              href={randomboxApi.bizhostExportUrl([...selected])}
            >
              엑셀 다운로드
            </a>
            <button
              type="button"
              className="btn btn--block"
              disabled={applyDelivery.isPending}
              onClick={() => applyDelivery.mutate()}
            >
              {applyDelivery.isPending ? '처리 중…' : '배송 반영'}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--block"
              disabled={stopDelivery.isPending}
              onClick={() => stopDelivery.mutate()}
            >
              {stopDelivery.isPending ? '처리 중…' : '배송 정지'}
            </button>
          </div>
          {writeError && <ErrorBox error={writeError} />}
        </Modal>
      )}

      <QueryState query={query} empty={query.data?.items.length === 0}>
        {query.data && (
          <>
          {/* 다음 페이지·다른 조건을 불러오는 동안은 이전 결과를 흐리게 둔다 - 지금
              보이는 줄이 새 조건의 결과가 아니라는 것이 드러난다. */}
          <div
            className={query.isPlaceholderData ? 'table-wrap rb-orders__wrap rb-orders__wrap--stale' : 'table-wrap rb-orders__wrap'}
            aria-busy={query.isPlaceholderData}
          >
            <table className="table rb-orders__table">
              <thead>
                <tr>
                  <th className="rb-orders__check">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      disabled={selectable.length === 0}
                      onChange={toggleAll}
                      aria-label="전체 선택"
                    />
                  </th>
                  <th className="rb-orders__idx">#</th>
                  <SortableTh label="주문번호" sortKey="orderNo" {...th} />
                  <SortableTh label="주문일" sortKey="orderDate" {...th} />
                  <th>랜덤박스</th>
                  <SortableTh label="수취인" sortKey="memberName" {...th} />
                  <SortableTh label="티켓" sortKey="ticket" {...th} align="right" />
                  <th style={{ textAlign: 'right' }}>상품금액</th>
                  <th style={{ textAlign: 'right' }}>배송비</th>
                  <SortableTh label="총결제금액" sortKey="totalAmount" {...th} align="right" />
                  <SortableTh label="상태" sortKey="orderStatus" {...th} />
                  <th>송장</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((order, i) => {
                  const checked = order.seq !== null && selected.has(order.seq);
                  const rowClass = [
                    order.orderNo ? 'table__row--clickable' : '',
                    checked ? 'rb-orders__row--selected' : '',
                    order.orderNo !== null && order.orderNo === lastOpened ? 'rb-orders__row--current' : '',
                    order.orderStatus === '주문취소' ? 'rb-orders__row--canceled' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return (
                  <tr
                    key={order.orderNo ?? i}
                    className={rowClass || undefined}
                    onClick={order.orderNo ? (e) => openRow(e, order.orderNo!) : undefined}
                  >
                    <td className="rb-orders__check">
                      {/* 원본과 같이 배송 반영이 끝난 건은 고를 수 없다. */}
                      {!order.deliveryResetFlag && order.seq !== null && (
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleOne(order.seq!)}
                          aria-label={`${order.orderNo ?? order.seq} 선택`}
                        />
                      )}
                    </td>
                    <td className="td-muted rb-orders__idx">{(page - 1) * query.data.perPage + i + 1}</td>
                    <td className="rb-orders__no">
                      {order.orderNo ? (
                        <Link
                          className="link"
                          to={detailPath(order.orderNo)}
                          state={{ background: location }}
                          onClick={() => setLastOpened(order.orderNo)}
                        >
                          {order.orderNo}
                        </Link>
                      ) : (
                        '-'
                      )}
                      {order.memo && <StatusBadge label="메모" tone="memo" title={order.memo} />}
                    </td>
                    <td className="td-muted rb-orders__date">{datetime(order.orderDate)}</td>
                    <td className="rb-orders__product" title={order.product.name ?? undefined}>
                      <ProductCell name={text(order.product.name)} thumbnail={order.product.thumbnail} />
                    </td>
                    <td className="rb-orders__member">
                      {order.member.idx !== null ? (
                        <button
                          type="button"
                          className="link"
                          title={`${order.member.name ?? ''} 님의 당첨 이력 보기`}
                          onClick={() =>
                            setCustomerModal({
                              idx: order.member.idx!,
                              name: order.member.name ?? '',
                            })
                          }
                        >
                          {text(order.member.name)}
                        </button>
                      ) : (
                        <span title={order.member.name ?? undefined}>{text(order.member.name)}</span>
                      )}
                      {/* 회원ID 자리 - randombox_member 에는 아이디가 없어 회원번호를 쓴다. */}
                      {order.member.idx !== null && <span className="rb-orders__member-idx">#{order.member.idx}</span>}
                    </td>
                    <td className="td-right">{num(order.ticket)}</td>
                    <td className="td-right">{money(order.unitAmount)}</td>
                    {/* 배송비는 대부분 0 이다 - 0 은 흐리게 두어 배송비가 붙은 주문만 눈에 들어오게 한다. */}
                    <td className={order.deliveryPrice ? 'td-right' : 'td-right td-muted'}>{money(order.deliveryPrice)}</td>
                    <td className="td-right rb-orders__total">{money(order.totalAmount)}</td>
                    <td>
                      <OrderStatusBadges order={order} />
                    </td>
                    <td>
                      {order.trackingNumber ? (
                        <code className="code">{order.trackingNumber}</code>
                      ) : (
                        <span className="td-muted">미등록</span>
                      )}
                    </td>
                    <td>
                      {order.orderNo ? (
                        <Link
                          className="link rb-orders__detail"
                          to={detailPath(order.orderNo)}
                          state={{ background: location }}
                          onClick={() => setLastOpened(order.orderNo)}
                        >
                          상세보기
                        </Link>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pagination
            page={query.data.page}
            totalPages={query.data.totalPages}
            total={query.data.total}
            onChange={(p) => update({ page: p }, false)}
          />
          </>
        )}
      </QueryState>

      {customerModal && (
        <CustomerPrizesModal member={customerModal} onClose={() => setCustomerModal(null)} />
      )}
    </section>
  );
}

/** 원본: randombox/order/randombox_order_customer.php — 목록에서 회원을 누르면 그 사람의 당첨 이력만 모달로 보여준다. */
function CustomerPrizesModal({
  member,
  onClose,
}: {
  member: { idx: number; name: string };
  onClose: () => void;
}) {
  const query = useQuery({
    queryKey: ['randombox', 'customerPrizes', member.idx],
    queryFn: () => randomboxApi.customerPrizes(member.idx),
  });

  return (
    <Modal
      title="고객별 당첨 리스트"
      titleExtra={`${member.name} (#${member.idx})`}
      onClose={onClose}
      wide
    >
      <QueryState
        query={query}
        empty={query.data?.items.length === 0}
        emptyMessage="당첨 이력이 없습니다."
      >
        {query.data && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>랜덤박스명</th>
                  <th>등급</th>
                  <th>상품명</th>
                  <th style={{ textAlign: 'right' }}>티켓장수</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((it, i) => (
                  <tr key={it.prizeId ?? i}>
                    <td>{text(it.productName)}</td>
                    <td><GradeBadge grade={it.grade} palette="customerHistory" /></td>
                    <td>{text(it.prizeTitle)}</td>
                    <td className="td-right">{num(it.ticketCount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="row--total">
                  <td colSpan={3} style={{ textAlign: 'right' }}>합계</td>
                  <td className="td-right">{num(query.data.totalTickets)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </QueryState>
    </Modal>
  );
}
